/**
 * ChartCaptureProvider - Context for capturing chart images
 *
 * Provides a way for chart components to register themselves for PDF capture,
 * and for the PDF generation code to capture all registered charts.
 * @remarks Web-only implementation using html2canvas
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import { Platform } from "react-native";

/**
 * Chart capture result with chart type and image data
 */
export interface ChartCaptureResult {
  readonly spiderChart: string | null;
  readonly dotPlot: string | null;
}

/**
 * Context value interface
 */
interface ChartCaptureContextValue {
  /** Register a ref object for the spider chart container */
  readonly registerSpiderChartRef: (
    ref: React.RefObject<HTMLDivElement | null>
  ) => void;
  /** Register a ref object for the dot plot container */
  readonly registerDotPlotRef: (
    ref: React.RefObject<HTMLDivElement | null>
  ) => void;
  /**
   * Resolve once both chart DOM nodes are mounted and registered, or `false`
   * if the timeout elapses first. Used by PDF export to wait for an on-demand
   * DataHub mount (SE-5062) before calling captureCharts.
   */
  readonly awaitChartsReady: (timeoutMs?: number) => Promise<boolean>;
  /** Capture all registered charts and return image data */
  readonly captureCharts: () => Promise<ChartCaptureResult>;
}

const ChartCaptureContext = createContext<ChartCaptureContextValue | null>(
  null
);

/**
 * Props for ChartCaptureProvider
 */
interface ChartCaptureProviderProps {
  readonly children: ReactNode;
}

/**
 * Provider component for chart capture functionality
 * @function ChartCaptureProvider
 * @param props - Provider props
 * @param props.children - Child components to wrap
 * @returns React provider component
 */
export function ChartCaptureProvider({
  children,
}: ChartCaptureProviderProps): React.ReactElement {
  const spiderChartRefObject =
    useRef<React.RefObject<HTMLDivElement | null> | null>(null);
  const dotPlotRefObject =
    useRef<React.RefObject<HTMLDivElement | null> | null>(null);

  const registerSpiderChartRef = useCallback(
    (ref: React.RefObject<HTMLDivElement | null>) => {
      // eslint-disable-next-line no-console -- Debug logging for chart capture
      console.log("[ChartCapture] registerSpiderChartRef:", !!ref);
      spiderChartRefObject.current = ref;
    },
    []
  );

  const registerDotPlotRef = useCallback(
    (ref: React.RefObject<HTMLDivElement | null>) => {
      // eslint-disable-next-line no-console -- Debug logging for chart capture
      console.log("[ChartCapture] registerDotPlotRef:", !!ref);
      dotPlotRefObject.current = ref;
    },
    []
  );

  const awaitChartsReady = useCallback(
    async (timeoutMs = 8000): Promise<boolean> => {
      // Capture is web-only; on native there is nothing to wait for.
      if (Platform.OS !== "web") {
        return false;
      }

      // Ready means both chart wrappers have mounted their DOM nodes and
      // registered them. Because the spider chart and dot plot only render once
      // DataHub's data has loaded, a mounted DOM node transitively proves the
      // data is ready — so this single check covers both "data loaded" and
      // "refs registered" (SE-5062 mount-before-ref race).
      const isReady = (): boolean =>
        Boolean(spiderChartRefObject.current?.current) &&
        Boolean(dotPlotRefObject.current?.current);

      if (isReady()) {
        return true;
      }

      const start = Date.now();
      return new Promise<boolean>(resolve => {
        const interval = setInterval(() => {
          if (isReady()) {
            clearInterval(interval);
            resolve(true);
          } else if (Date.now() - start >= timeoutMs) {
            // Timed out — let the caller proceed with whatever refs exist so the
            // export degrades to a chart-less PDF instead of hanging forever.
            clearInterval(interval);
            resolve(false);
          }
        }, 100);
      });
    },
    []
  );

  const captureCharts = useCallback(async (): Promise<ChartCaptureResult> => {
    const spiderElement = spiderChartRefObject.current?.current ?? null;
    const dotPlotElement = dotPlotRefObject.current?.current ?? null;

    // eslint-disable-next-line no-console -- Debug logging for chart capture
    console.log("[ChartCapture] captureCharts called", {
      hasSpiderRef: !!spiderElement,
      hasDotPlotRef: !!dotPlotElement,
      platform: Platform.OS,
    });

    if (Platform.OS !== "web") {
      return { spiderChart: null, dotPlot: null };
    }

    try {
      const html2canvas = (await import("html2canvas")).default;
      // eslint-disable-next-line no-console -- Debug logging for chart capture
      console.log("[ChartCapture] html2canvas loaded");

      const captureElement = async (
        element: HTMLDivElement | null
      ): Promise<string | null> => {
        if (!element) return null;

        try {
          const canvas = await html2canvas(element, {
            backgroundColor: null,
            scale: 2,
            logging: false,
            useCORS: true,
            allowTaint: true,
          });
          return canvas.toDataURL("image/png");
        } catch {
          return null;
        }
      };

      const [spiderChart, dotPlot] = await Promise.all([
        captureElement(spiderElement),
        captureElement(dotPlotElement),
      ]);

      return { spiderChart, dotPlot };
    } catch (error) {
      console.error("Failed to capture charts:", error);
      return { spiderChart: null, dotPlot: null };
    }
  }, []);

  const value = useMemo(
    () => ({
      registerSpiderChartRef,
      registerDotPlotRef,
      awaitChartsReady,
      captureCharts,
    }),
    [
      registerSpiderChartRef,
      registerDotPlotRef,
      awaitChartsReady,
      captureCharts,
    ]
  );

  return (
    <ChartCaptureContext.Provider value={value}>
      {children}
    </ChartCaptureContext.Provider>
  );
}

/**
 * Hook to access chart capture context
 * @returns Chart capture context value
 * @throws Error if used outside of ChartCaptureProvider
 */
export function useChartCapture(): ChartCaptureContextValue {
  const context = useContext(ChartCaptureContext);
  if (!context) {
    throw new Error("useChartCapture must be used within ChartCaptureProvider");
  }
  return context;
}

/**
 * Hook that returns a safe version of chart capture context
 * Returns null operations if not within provider (for optional usage)
 * @returns Chart capture context value with safe defaults
 */
export function useChartCaptureOptional(): ChartCaptureContextValue {
  const context = useContext(ChartCaptureContext);
  return (
    context ?? {
      registerSpiderChartRef: (
        _ref: React.RefObject<HTMLDivElement | null>
      ) => {},
      registerDotPlotRef: (_ref: React.RefObject<HTMLDivElement | null>) => {},
      awaitChartsReady: async () => false,
      captureCharts: async () => ({ spiderChart: null, dotPlot: null }),
    }
  );
}

ChartCaptureProvider.displayName = "ChartCaptureProvider";
